import React, { useState } from 'react';
import { Search, Sparkles, MapPin, Clock, Car, ExternalLink, ArrowRight, Compass, X, RotateCcw, AlertCircle } from 'lucide-react';
import { Attraction, Category } from '../types';
import { CATEGORIES } from '../data/attractions';
import { useLanguage } from '../context/LanguageContext';
import { getLocalizedAttraction } from '../utils/localization';

interface AttractionsDirectoryProps {
  attractions: Attraction[];
  selectedCategory: Category;
  onCategorySelect: (cat: Category) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onAttractionClick: (attraction: Attraction) => void;
}

const CATEGORY_LABELS_EN: Record<string, string> = {
  todos: 'All Destinations',
  playas: 'Iconic Beaches',
  cenotes: 'Mystic Cenotes',
  'vida-nocturna': 'Nightlife & Clubs',
  shopping: 'Shopping & Plazas',
  roadtrips: 'Road Trips by Car',
  gastronomia: 'Cuisine & Markets',
};

const CATEGORY_LABELS_PT: Record<string, string> = {
  todos: 'Todos os Lugares',
  playas: 'Praias Icônicas',
  cenotes: 'Cenotes Místicos',
  'vida-nocturna': 'Vida Noturna e Festas',
  shopping: 'Compras e Lazer',
  roadtrips: 'Roteiros de Carro',
  gastronomia: 'Gastronomia e Mercados',
};

export const AttractionsDirectory: React.FC<AttractionsDirectoryProps> = ({
  attractions,
  selectedCategory,
  onCategorySelect,
  searchQuery,
  onSearchChange,
  onAttractionClick
}) => {
  const [sortBy, setSortBy] = useState<'rating' | 'distance'>('rating');
  const { lang, t } = useLanguage();

  // Category counts calculation
  const categoryCounts = React.useMemo(() => {
    const counts: Record<string, number> = { todos: attractions.length };
    for (const cat of CATEGORIES) {
      if (cat.id !== 'todos') {
        counts[cat.id] = attractions.filter(a => a.category === cat.id).length;
      }
    }
    return counts;
  }, [attractions]);

  const filtered = attractions.filter((item) => {
    const matchesCategory =
      selectedCategory === 'todos' || item.category === selectedCategory;

    const locItem = getLocalizedAttraction(item, lang);
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.title.toLowerCase().includes(q) ||
      locItem.title.toLowerCase().includes(q) ||
      item.shortDescription.toLowerCase().includes(q) ||
      locItem.shortDescription.toLowerCase().includes(q) ||
      item.tag.toLowerCase().includes(q) ||
      locItem.tag.toLowerCase().includes(q) ||
      item.whyByCar.toLowerCase().includes(q) ||
      locItem.whyByCar.toLowerCase().includes(q);

    return matchesCategory && matchesSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'distance') {
      const diff = a.drivingTimeMinutes - b.drivingTimeMinutes;
      if (diff !== 0) return diff;
      return a.id.localeCompare(b.id);
    }
    const diff = b.rating - a.rating;
    if (diff !== 0) return diff;
    return a.id.localeCompare(b.id);
  });

  const handleResetAll = () => {
    onSearchChange('');
    onCategorySelect('todos');
  };

  const hasActiveFilters = selectedCategory !== 'todos' || searchQuery.trim().length > 0;

  return (
    <section id="destinos" className="py-16 bg-[#F7F3EA] border-b border-[#ede6d8] scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#044e45]/10 text-[#044e45] mb-2 border border-[#044e45]/20">
              <Compass className="w-3.5 h-3.5 text-[#0F766E]" />
              {t.directory.badge}
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#073B4C] font-['Outfit',sans-serif]">
              {t.directory.title}
            </h2>
            <p className="text-sm text-[#4A5A5F] mt-1 max-w-2xl">
              {t.directory.subtitle}
            </p>
          </div>

          {/* Quick Search & Sort Bar in Directory */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Inline search */}
            <div className="relative min-w-[240px] sm:min-w-[280px]">
              <Search className="w-4 h-4 text-[#4A5A5F] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t.directory.searchPlaceholder}
                className="w-full pl-10 pr-8 py-2 bg-white text-[#172A32] placeholder-[#4A5A5F]/70 rounded-xl text-xs sm:text-sm font-medium border border-[#ede6d8] focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] shadow-xs"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => onSearchChange('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#4A5A5F] hover:text-[#172A32] p-1 cursor-pointer"
                  title={lang === 'en' ? 'Clear search' : lang === 'pt' ? 'Limpar busca' : 'Limpiar búsqueda'}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Sort Controls */}
            <div className="flex items-center gap-2">
              <div className="bg-white border border-[#ede6d8] rounded-xl p-1 flex text-xs font-semibold shadow-xs">
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                    sortBy === 'rating'
                      ? 'bg-[#0F766E] text-white font-bold shadow-xs'
                      : 'text-[#4A5A5F] hover:text-[#172A32]'
                  }`}
                >
                  {t.directory.sortRating}
                </button>
                <button
                  onClick={() => setSortBy('distance')}
                  className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                    sortBy === 'distance'
                      ? 'bg-[#0F766E] text-white font-bold shadow-xs'
                      : 'text-[#4A5A5F] hover:text-[#172A32]'
                  }`}
                >
                  {t.directory.sortTime}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Category Selector Tabs with Item Counts */}
        <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-5 scrollbar-none">
          {CATEGORIES.map((cat) => {
            const isActive = selectedCategory === cat.id;
            const count = categoryCounts[cat.id] || 0;
            const label = lang === 'en' ? (CATEGORY_LABELS_EN[cat.id] || cat.label) : lang === 'pt' ? (CATEGORY_LABELS_PT[cat.id] || cat.label) : cat.label;
            return (
              <button
                key={cat.id}
                onClick={() => onCategorySelect(cat.id as Category)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 shadow-xs ${
                  isActive
                    ? 'bg-[#073B4C] text-white shadow-md border border-[#073B4C] ring-2 ring-[#D6A84B]'
                    : 'bg-white text-[#4A5A5F] hover:bg-white/80 hover:text-[#073B4C] border border-[#ede6d8]'
                }`}
              >
                <span>{label}</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-extrabold ${
                  isActive ? 'bg-[#D6A84B] text-[#073B4C]' : 'bg-[#ede6d8] text-[#172A32]'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active Filters Summary Bar */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6 p-3 bg-white rounded-xl border border-[#ede6d8] shadow-xs text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold text-[#073B4C]">
                {lang === 'en' ? `Showing ${sorted.length} ${sorted.length === 1 ? 'destination' : 'destinations'}` : lang === 'pt' ? `Mostrando ${sorted.length} ${sorted.length === 1 ? 'destino' : 'destinos'}` : `Mostrando ${sorted.length} ${sorted.length === 1 ? 'destino' : 'destinos'}`}
              </span>

              {selectedCategory !== 'todos' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#044e45]/10 text-[#044e45] font-bold">
                  {lang === 'en' ? 'Category: ' : lang === 'pt' ? 'Categoria: ' : 'Categoría: '}
                  {lang === 'en' ? CATEGORY_LABELS_EN[selectedCategory] : lang === 'pt' ? CATEGORY_LABELS_PT[selectedCategory] : CATEGORIES.find(c => c.id === selectedCategory)?.label}
                  <button 
                    onClick={() => onCategorySelect('todos')}
                    className="hover:text-[#073B4C] ml-0.5 cursor-pointer"
                    title={lang === 'en' ? 'View all categories' : lang === 'pt' ? 'Ver todas as categorias' : 'Ver todas las categorías'}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}

              {searchQuery.trim() && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#D6A84B]/20 text-[#8f6d28] font-bold">
                  {lang === 'en' ? 'Search: ' : lang === 'pt' ? 'Busca: ' : 'Búsqueda: '}"{searchQuery}"
                  <button 
                    onClick={() => onSearchChange('')}
                    className="hover:text-[#172A32] ml-0.5 cursor-pointer"
                    title={lang === 'en' ? 'Clear query' : lang === 'pt' ? 'Remover busca' : 'Quitar búsqueda'}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
            </div>

            <button
              onClick={handleResetAll}
              className="text-xs font-bold text-[#A8471A] hover:text-[#913a12] hover:underline cursor-pointer flex items-center gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>
                {lang === 'en' ? `Reset filters (${attractions.length} total)` : lang === 'pt' ? `Restablecer filtros (${attractions.length} totales)` : `Restablecer filtros (${attractions.length} totales)`}
              </span>
            </button>
          </div>
        )}

        {/* Grid of Cards */}
        {sorted.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#ede6d8] p-8 max-w-xl mx-auto shadow-sm">
            <AlertCircle className="w-12 h-12 text-[#A8471A] mx-auto mb-3" />
            <p className="text-lg font-bold text-[#172A32]">
              {lang === 'en' ? 'No places found with these filters.' : lang === 'pt' ? 'Nenhum local encontrado com estes filtros.' : 'No se encontraron lugares con estos filtros.'}
            </p>
            <p className="text-xs text-[#4A5A5F] mt-1.5 leading-relaxed">
              {searchQuery.trim()
                ? (lang === 'en' ? `No destinations in this category matching "${searchQuery}".` : lang === 'pt' ? `No hay destinos en la categoría seleccionada que coincidan con "${searchQuery}".` : `No hay destinos en la categoría seleccionada que coincidan con "${searchQuery}".`)
                : (lang === 'en' ? 'No destinations available in this category.' : lang === 'pt' ? 'Não há destinos disponíveis para esta categoria.' : 'No hay destinos disponibles para esta categoria.')}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 mt-6">
              <button
                onClick={handleResetAll}
                className="w-full sm:w-auto px-5 py-2.5 text-xs font-bold text-white bg-[#0F766E] hover:bg-[#0d695d] rounded-xl cursor-pointer shadow-sm transition-all"
              >
                {lang === 'en' ? `View all ${attractions.length} destinations` : lang === 'pt' ? `Ver todos os ${attractions.length} lugares` : `Ver todos los ${attractions.length} lugares`}
              </button>
              {selectedCategory !== 'todos' && searchQuery.trim() && (
                <button
                  onClick={() => onCategorySelect('todos')}
                  className="w-full sm:w-auto px-4 py-2.5 text-xs font-semibold text-[#073B4C] bg-[#F7F3EA] hover:bg-[#ede6d8] rounded-xl border border-[#ede6d8] cursor-pointer transition-all"
                >
                  {lang === 'en' ? `Search "${searchQuery}" in all categories` : lang === 'pt' ? `Buscar "${searchQuery}" em todas as categorias` : `Buscar "${searchQuery}" en todas las categorías`}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sorted.map((rawItem) => {
              const item = getLocalizedAttraction(rawItem, lang);
              return (
                <div
                  key={rawItem.id}
                  onClick={() => onAttractionClick(rawItem)}
                  style={{ contentVisibility: 'auto', containIntrinsicSize: '0 420px' }}
                  className="group bg-white rounded-2xl border border-[#ede6d8] shadow-sm hover:shadow-xl hover:border-[#0F766E]/40 transition-all duration-300 flex flex-col overflow-hidden cursor-pointer transform hover:-translate-y-1"
                >
                  {/* Image Container */}
                  <div className="relative h-48 w-full overflow-hidden bg-[#F7F3EA]">
                    <img
                      src={item.image}
                      alt={item.title}
                      width={400}
                      height={192}
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#073B4C]/80 via-transparent to-transparent" />

                    {/* Top Badges */}
                    <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-white/95 text-[#073B4C] backdrop-blur-xs shadow-sm">
                        {item.tag}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#073B4C]/70 text-[#D6A84B] backdrop-blur-xs">
                        ⭐ {item.rating}
                      </span>
                    </div>

                    {/* Bottom Image Info */}
                    <div className="absolute bottom-2.5 left-3 right-3 text-white flex items-center justify-between text-xs">
                      <span className="font-semibold flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-[#2DD4BF]" />
                        {item.distanceFromCancun}
                      </span>
                      <span className="bg-[#0F766E] text-white px-2 py-0.5 rounded text-xs font-bold shadow-xs">
                        {item.priceRange}
                      </span>
                    </div>
                  </div>

                  {/* Content Details */}
                  <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <h3 className="text-base font-bold text-[#172A32] font-['Outfit',sans-serif] group-hover:text-[#0F766E] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-sm text-[#4A5A5F] line-clamp-4 mt-2 leading-relaxed">
                        {item.shortDescription}
                      </p>
                    </div>

                    {/* Card Footer Actions */}
                    <div className="pt-3 border-t border-[#ede6d8] flex items-center justify-end text-xs">
                      <span className="text-[#0F766E] font-bold flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                        {t.directory.viewDetails} <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
